import type { ReactNode } from 'react';

// ─── Master Console 2.0 — Shared UI components ──────────────────────────────
// All components use CSS classes (sk-mc-*) defined in styles.css,
// no inline styles — consistent with the dark navy + cyan theme.

// ─── KpiTile ────────────────────────────────────────────────────────────────

export function KpiTile({
  label,
  value,
  sub,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'error' | 'info';
  icon?: ReactNode;
}) {
  return (
    <div className={`sk-mc-kpi sk-mc-kpi--${tone}`}>
      {icon ? <span className="sk-mc-kpi__icon">{icon}</span> : null}
      <div className="sk-mc-kpi__body">
        <p className="sk-mc-kpi__label">{label}</p>
        <p className="sk-mc-kpi__value">{value}</p>
        {sub ? <p className="sk-mc-kpi__sub">{sub}</p> : null}
      </div>
    </div>
  );
}

// ─── StatusBadge ───────────────────────────────────────────────────────────

export function StatusBadge({ status }: { status: string }) {
  const s = String(status || '').toUpperCase();
  const tone =
    s === 'ACTIVE' ? 'ok' :
    s === 'TRIAL' ? 'info' :
    s === 'PAST_DUE' ? 'warn' :
    s === 'SUSPENDED' ? 'error' :
    s === 'RESOLVED' || s === 'CLOSED' ? 'ok' :
    s === 'OPEN' ? 'warn' :
    s === 'IN_PROGRESS' ? 'info' :
    'neutral';
  const label =
    s === 'ACTIVE' ? 'Attivo' :
    s === 'TRIAL' ? 'Trial' :
    s === 'PAST_DUE' ? 'Past due' :
    s === 'SUSPENDED' ? 'Sospeso' :
    s === 'RESOLVED' ? 'Risolto' :
    s === 'CLOSED' ? 'Chiuso' :
    s === 'OPEN' ? 'Aperto' :
    s === 'IN_PROGRESS' ? 'In corso' :
    s || '—';
  return <span className={`sk-mc-badge sk-mc-badge--${tone}`}>{label}</span>;
}

// ─── CountBadge (for sidebar) ──────────────────────────────────────────────

export function CountBadge({ count, tone = 'error' }: { count: number; tone?: 'error' | 'warn' | 'info' }) {
  if (!count || count <= 0) return null;
  return <span className={`sk-mc-count sk-mc-count--${tone}`}>{count > 99 ? '99+' : count}</span>;
}

// ─── TabBar ────────────────────────────────────────────────────────────────

export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ key: T; label: string; count?: number }>;
  active: T;
  onChange: (key: T) => void;
}) {
  return (
    <div className="sk-mc-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`sk-mc-tab${tab.key === active ? ' sk-mc-tab--active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
          {tab.count != null && tab.count > 0 ? (
            <span className="sk-mc-tab__count">{tab.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

// ─── AlertPanel ────────────────────────────────────────────────────────────

export function AlertPanel({
  alerts,
}: {
  alerts: Array<{ tone: 'warn' | 'error' | 'info'; title: string; detail?: string }>;
}) {
  if (!alerts.length) {
    return (
      <div className="sk-mc-alerts">
        <div className="sk-mc-alert sk-mc-alert--ok">
          <span className="sk-mc-alert__icon">✓</span>
          <div>
            <p className="sk-mc-alert__title">Tutto sotto controllo</p>
            <p className="sk-mc-alert__detail">Nessun alert attivo.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="sk-mc-alerts">
      {alerts.map((alert, i) => (
        <div key={i} className={`sk-mc-alert sk-mc-alert--${alert.tone}`}>
          <span className="sk-mc-alert__icon">
            {alert.tone === 'error' ? '✕' : alert.tone === 'warn' ? '⚠' : 'ℹ'}
          </span>
          <div>
            <p className="sk-mc-alert__title">{alert.title}</p>
            {alert.detail ? <p className="sk-mc-alert__detail">{alert.detail}</p> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ActivityFeed ──────────────────────────────────────────────────────────

export function ActivityFeed({
  items,
}: {
  items: Array<{ id: number | string; action: string; actor?: string | null; time: string }>;
}) {
  if (!items.length) {
    return <p className="sk-mc-hint">Nessuna attività recente.</p>;
  }
  return (
    <ul className="sk-mc-feed">
      {items.map((item) => (
        <li key={item.id} className="sk-mc-feed__item">
          <span className="sk-mc-feed__dot" />
          <div className="sk-mc-feed__body">
            <p className="sk-mc-feed__action">{item.action}</p>
            <p className="sk-mc-feed__meta">
              {item.time}
              {item.actor ? ` · ${item.actor}` : ''}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ─── FilterBar ─────────────────────────────────────────────────────────────

export function FilterBar({
  children,
}: {
  children: ReactNode;
}) {
  return <div className="sk-mc-filterbar">{children}</div>;
}

// ─── ApiHealthBadge ────────────────────────────────────────────────────────

export function ApiHealthBadge({ online }: { online: boolean | null }) {
  if (online === null) {
    return <span className="sk-mc-health sk-mc-health--checking">●</span>;
  }
  return (
    <span className={`sk-mc-health ${online ? 'sk-mc-health--online' : 'sk-mc-health--offline'}`}>
      <span className="sk-mc-health__dot" />
      {online ? 'API online' : 'API offline'}
    </span>
  );
}

// ─── PageHeader ────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="sk-mc-header">
      <div>
        <h1 className="sk-mc-header__title">{title}</h1>
        {subtitle ? <p className="sk-mc-header__sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="sk-mc-header__actions">{actions}</div> : null}
    </header>
  );
}

// ─── SectionCard ───────────────────────────────────────────────────────────

export function SectionCard({
  title,
  actions,
  children,
  dense,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}) {
  return (
    <section className={`sk-mc-card${dense ? ' sk-mc-card--dense' : ''}`}>
      {title || actions ? (
        <header className="sk-mc-card__head">
          {title ? <h2 className="sk-mc-card__title">{title}</h2> : null}
          {actions ? <div className="sk-mc-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className="sk-mc-card__body">{children}</div>
    </section>
  );
}
